// ابزارهای روانپزشکی و روان‌درمانی (AP-CLN-03؛ FLOW-07b): نشان «بسیار حساس» یادداشت روان‌درمانی (US-CLN-100)، دیالوگ ارزیابی خطر با برنامه ایمنی
// الزامی برای High و Imminent و تماس اضطراری از پرونده PAT (US-CLN-114)، پنل روان کنار یادداشت (دکمه ثابت ارزیابی خطر، آخرین سطح، نمودار کوچک پیامد —
// US-CLN-116)، ثبت پیشرفت اهداف طرح (AC-CLN-115.2)، نمودارهای روند و پیامد (SCR-STAFF-509؛ US-CLN-091، 116)، بستن پیگیری خطر (BR-CLN-112) و انتخاب
// یادداشت حساس برای خروجی با تأیید (AC-CLN-100.2). داده HighlySensitive فقط از سرور با سیاست دید و Audit مشاهده می‌آید؛ هیچ محتوای بالینی در URL نیست.
import { alpha, useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  unwrap,
  type ClnEncounterView,
  type ClnGoalProgressRating,
  type ClnOutcomeSeriesView,
  type ClnOutcomesResponse,
  type ClnRiskLevel,
  type ClnSafetyContactKind,
  type ClnSafetyPlanRequest,
  type ClnTrendSeriesView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, SampleBadge, toJalali, toJalaliDateTime, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { clnKeys, num } from './common';

export const RISK_LEVELS: readonly ClnRiskLevel[] = ['Low', 'Moderate', 'High', 'Imminent'];
const CONTACT_KINDS: readonly ClnSafetyContactKind[] = ['Person', 'Place', 'Professional', 'Agency'];
const RATINGS: readonly ClnGoalProgressRating[] = ['NotStarted', 'Regressed', 'NoChange', 'Partial', 'Substantial', 'Achieved'];
/** تخصص‌های بسته روان (قالب‌های نمونه psych-*). */
const PSYCH_SPECIALTIES = ['psychiatry', 'psychology'];

export const psychKeys = {
  risk: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'risk'] as const,
  outcomes: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'outcomes'] as const,
  trends: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'trends'] as const,
  contacts: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'emergency-contacts'] as const,
  goals: (tenantId: string | undefined, planId: string) => ['clinical', tenantId, 'plan', planId, 'goal-progress'] as const,
};

const LEVEL_COLOR: Record<ClnRiskLevel, 'success' | 'warning' | 'error'> = { Low: 'success', Moderate: 'warning', High: 'error', Imminent: 'error' };

/** نشان «بسیار حساس» (BR-CLN-100). */
export function SensitiveBadge() {
  const { t } = useTranslation('cln');
  return <Chip size="small" color="secondary" variant="outlined" icon={<LockOutlinedIcon />} label={t('cln:psych.highly_sensitive')} data-testid="cln-hs-badge" />;
}

export function RiskLevelChip({ level }: { level: ClnRiskLevel }) {
  const { t } = useTranslation('cln');
  return <Chip size="small" color={LEVEL_COLOR[level]} variant={level === 'Imminent' ? 'filled' : 'outlined'} label={t(`cln:psych.level.${level}`)} data-testid="cln-risk-level" data-level={level} />;
}

/** زمینه روان مواجهه: قالب تخصص روانپزشکی یا روانشناسی، یا یادداشت روان‌درمانی. */
export function isPsychContext(note: { kind?: string | null; template: { specialtyCode?: string | null } } | undefined, encounter: Pick<ClnEncounterView, 'counselingSession'> | undefined): boolean {
  return Boolean(encounter?.counselingSession || note?.kind === 'Psychotherapy' || (note?.template.specialtyCode && PSYCH_SPECIALTIES.includes(note.template.specialtyCode)));
}

interface ContactDraft {
  kind: ClnSafetyContactKind;
  name: string;
  phone: string;
}

interface PlanDraft {
  warningSigns: string;
  coping: string;
  contacts: ContactDraft[];
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelation: string;
  meansSafety: string;
  reasons: string;
}

const emptyPlan = (): PlanDraft => ({ warningSigns: '', coping: '', contacts: [{ kind: 'Person', name: '', phone: '' }], emergencyName: '', emergencyPhone: '', emergencyRelation: '', meansSafety: '', reasons: '' });

const lines = (value: string) => value.split('\n').map((line) => line.trim()).filter(Boolean);

export function planBody(plan: PlanDraft): ClnSafetyPlanRequest {
  return {
    warningSigns: lines(plan.warningSigns),
    copingStrategies: lines(plan.coping),
    contacts: plan.contacts.filter((contact) => contact.name.trim()).map((contact) => ({ kind: contact.kind, name: contact.name.trim(), phone: contact.phone.trim() || null })),
    emergencyContact: plan.emergencyName.trim() || plan.emergencyPhone.trim() ? { name: plan.emergencyName.trim(), phone: plan.emergencyPhone.trim(), relation: plan.emergencyRelation.trim() || null } : null,
    meansSafety: plan.meansSafety.trim() || null,
    reasonsForLiving: plan.reasons.trim() || null,
  };
}

/** بخش‌های برنامه ایمنی ساختاریافته (AC-CLN-114.2) با تماس اضطراری پیشنهادی از PAT (AC-CLN-114.3). */
function SafetyPlanFields({ patientId, plan, onChange }: { patientId: string; plan: PlanDraft; onChange: (plan: PlanDraft) => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const candidates = useQuery({
    queryKey: psychKeys.contacts(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/emergency-contacts', { params: { path: { patientId } } })),
    retry: false,
  });
  const set = (patch: Partial<PlanDraft>) => onChange({ ...plan, ...patch });
  return (
    <Stack spacing={1.5} data-testid="cln-safety-plan">
      <Typography variant="subtitle1" component="h3">
        {t('cln:psych.safety_plan')}
      </Typography>
      <TextField required multiline minRows={2} label={t('cln:psych.warning_signs')} helperText={t('cln:psych.one_per_line')} value={plan.warningSigns} onChange={(event) => set({ warningSigns: event.target.value })} slotProps={{ htmlInput: { 'data-testid': 'cln-plan-warning-signs' } }} />
      <TextField required multiline minRows={2} label={t('cln:psych.coping')} helperText={t('cln:psych.one_per_line')} value={plan.coping} onChange={(event) => set({ coping: event.target.value })} slotProps={{ htmlInput: { 'data-testid': 'cln-plan-coping' } }} />
      <Typography variant="body2">{t('cln:psych.contacts')}</Typography>
      {plan.contacts.map((contact, index) => (
        <Stack key={index} direction="row" spacing={1} data-testid="cln-plan-contact">
          <TextField select size="small" label={t('cln:psych.contact_kind')} value={contact.kind} onChange={(event) => set({ contacts: plan.contacts.map((item, i) => (i === index ? { ...item, kind: event.target.value as ClnSafetyContactKind } : item)) })} sx={{ minWidth: 140 }}>
            {CONTACT_KINDS.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {t(`cln:psych.contact_kinds.${kind}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" fullWidth label={t('cln:psych.contact_name')} value={contact.name} onChange={(event) => set({ contacts: plan.contacts.map((item, i) => (i === index ? { ...item, name: event.target.value } : item)) })} slotProps={{ htmlInput: { maxLength: 120, 'data-testid': 'cln-plan-contact-name' } }} />
          <TextField size="small" required={contact.kind !== 'Place'} label={t('cln:psych.phone')} value={contact.phone} onChange={(event) => set({ contacts: plan.contacts.map((item, i) => (i === index ? { ...item, phone: event.target.value } : item)) })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'tel', 'data-testid': 'cln-plan-contact-phone' } }} />
          <IconButton aria-label={t('cln:psych.remove_contact')} onClick={() => set({ contacts: plan.contacts.filter((_item, i) => i !== index) })} disabled={plan.contacts.length === 1}>
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      ))}
      <Button startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }} onClick={() => set({ contacts: [...plan.contacts, { kind: 'Person', name: '', phone: '' }] })}>
        {t('cln:psych.add_contact')}
      </Button>
      <Typography variant="body2">{t('cln:psych.emergency_contact')}</Typography>
      {(candidates.data?.items ?? []).length > 0 && (
        <TextField
          select
          size="small"
          label={t('cln:psych.from_patient_file')}
          value=""
          onChange={(event) => {
            const candidate = candidates.data?.items[Number(event.target.value)];
            if (candidate) {
              set({ emergencyName: candidate.name ?? plan.emergencyName, emergencyPhone: candidate.phone ?? '', emergencyRelation: t(`cln:psych.contact_source.${candidate.source}`) });
            }
          }}
          slotProps={{ htmlInput: { 'data-testid': 'cln-plan-emergency-candidates' } }}
        >
          {(candidates.data?.items ?? []).map((candidate, index) => (
            <MenuItem key={index} value={String(index)}>
              {[candidate.name, candidate.phone, t(`cln:psych.contact_source.${candidate.source}`)].filter(Boolean).join(' · ')}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Stack direction="row" spacing={1}>
        <TextField required size="small" fullWidth label={t('cln:psych.contact_name')} value={plan.emergencyName} onChange={(event) => set({ emergencyName: event.target.value })} slotProps={{ htmlInput: { maxLength: 120, 'data-testid': 'cln-plan-emergency-name' } }} />
        <TextField required size="small" label={t('cln:psych.phone')} value={plan.emergencyPhone} onChange={(event) => set({ emergencyPhone: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'tel', 'data-testid': 'cln-plan-emergency-phone' } }} />
        <TextField size="small" label={t('cln:psych.relation')} value={plan.emergencyRelation} onChange={(event) => set({ emergencyRelation: event.target.value })} slotProps={{ htmlInput: { maxLength: 60 } }} />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t('cln:psych.emergency_hint')}
      </Typography>
      <TextField multiline minRows={1} label={t('cln:psych.means_safety')} value={plan.meansSafety} onChange={(event) => set({ meansSafety: event.target.value })} slotProps={{ htmlInput: { maxLength: 2000 } }} />
      <TextField multiline minRows={1} label={t('cln:psych.reasons_for_living')} value={plan.reasons} onChange={(event) => set({ reasons: event.target.value })} slotProps={{ htmlInput: { maxLength: 2000 } }} />
    </Stack>
  );
}

function FactorPicker({ label, codes, value, onChange, testId }: { label: string; codes: readonly string[]; value: string[]; onChange: (value: string[]) => void; testId: string }) {
  const { t } = useTranslation('cln');
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" data-testid={testId}>
        {codes.map((code) => {
          const selected = value.includes(code);
          return (
            <Chip
              key={code}
              size="small"
              clickable
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              label={t(`cln:psych.factors.${code}`)}
              onClick={() => onChange(selected ? value.filter((item) => item !== code) : [...value, code])}
              aria-pressed={selected}
              data-code={code}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

/** ارزیابی خطر (AC-CLN-114.1، 114.2): High یا Imminent ← برنامه ایمنی باز و الزامی؛ اعلان Critical و پیگیری در سرور. */
export function RiskAssessmentDialog({ patientId, encounterId, onClose, onSaved }: { patientId: string; encounterId?: string | null; onClose: () => void; onSaved?: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const codes = useQuery({
    queryKey: psychKeys.risk(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/risk-assessments', { params: { path: { patientId } } })),
    retry: false,
  });
  const [level, setLevel] = useState<ClnRiskLevel | ''>('');
  const [risks, setRisks] = useState<string[]>([]);
  const [protective, setProtective] = useState<string[]>([]);
  const [other, setOther] = useState('');
  const [judgment, setJudgment] = useState('');
  const [plan, setPlan] = useState<PlanDraft>(emptyPlan);
  const high = level === 'High' || level === 'Imminent';
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/patients/{patientId}/risk-assessments', {
          params: { path: { patientId } },
          headers: { 'Idempotency-Key': key.current() },
          body: {
            level: level || null,
            riskFactors: risks,
            protectiveFactors: protective,
            otherFactors: other.trim() || null,
            judgment: judgment.trim() || null,
            encounterId: encounterId ?? null,
            formSubmissionId: null,
            safetyPlan: high ? planBody(plan) : null,
          },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clnKeys.patient(session?.tenantId, patientId) });
      void queryClient.invalidateQueries({ queryKey: clnKeys.pending(session?.tenantId) });
      onSaved?.();
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="cln:psych.risk_assessment" onSubmit={() => (level ? save.mutate() : undefined)} onClose={onClose} isDirty={Boolean(level || judgment)} isSubmitting={save.isPending} submitError={save.error} maxWidth="md">
      <Alert severity="info" icon={<LockOutlinedIcon />}>
        {t('cln:psych.risk_private_hint')}
      </Alert>
      <TextField select required label={t('cln:psych.level_label')} value={level} onChange={(event) => setLevel(event.target.value as ClnRiskLevel)} slotProps={{ htmlInput: { 'data-testid': 'cln-risk-level-select' } }}>
        {RISK_LEVELS.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`cln:psych.level.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <FactorPicker label={t('cln:psych.risk_factors')} codes={codes.data?.riskFactorCodes ?? []} value={risks} onChange={setRisks} testId="cln-risk-factors" />
      <FactorPicker label={t('cln:psych.protective_factors')} codes={codes.data?.protectiveFactorCodes ?? []} value={protective} onChange={setProtective} testId="cln-protective-factors" />
      {(risks.includes('other') || protective.includes('other')) && <TextField required label={t('cln:psych.other_factors')} value={other} onChange={(event) => setOther(event.target.value)} slotProps={{ htmlInput: { maxLength: 1000 } }} />}
      <TextField required multiline minRows={3} label={t('cln:psych.judgment')} value={judgment} onChange={(event) => setJudgment(event.target.value)} slotProps={{ htmlInput: { maxLength: 4000, 'data-testid': 'cln-risk-judgment' } }} />
      {high && (
        <>
          <Alert severity="error" data-testid="cln-safety-plan-required">
            {t('cln:psych.safety_plan_required')}
          </Alert>
          <SafetyPlanFields patientId={patientId} plan={plan} onChange={setPlan} />
        </>
      )}
    </FormDialog>
  );
}

/** نسخه تازه برنامه ایمنی بدون ارزیابی تازه (AC-CLN-114.3). */
export function SafetyPlanDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [plan, setPlan] = useState<PlanDraft>(emptyPlan);
  const save = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/safety-plans', { params: { path: { patientId } }, headers: { 'Idempotency-Key': key.current() }, body: planBody(plan) })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: psychKeys.risk(session?.tenantId, patientId) });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="cln:psych.update_safety_plan" onSubmit={() => save.mutate()} onClose={onClose} isDirty={Boolean(plan.warningSigns || plan.coping)} isSubmitting={save.isPending} submitError={save.error} maxWidth="md">
      <SafetyPlanFields patientId={patientId} plan={plan} onChange={setPlan} />
    </FormDialog>
  );
}

/** بستن پیگیری بالینی خطر با یادداشت (BR-CLN-112). */
export function RiskFollowUpDialog({ assessmentId, onClose }: { assessmentId: string; onClose: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/clinical/risk-assessments/{id}:complete-follow-up', { params: { path: { id: assessmentId } }, body: { note: note.trim() || null } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clnKeys.pending(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: ['clinical', session?.tenantId, 'patient'] });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="cln:psych.complete_follow_up" onSubmit={() => (note.trim() ? save.mutate() : undefined)} onClose={onClose} isDirty={note !== ''} isSubmitting={save.isPending} submitError={save.error}>
      <TextField required autoFocus multiline minRows={3} label={t('cln:psych.follow_up_note')} value={note} onChange={(event) => setNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'cln-follow-up-note' } }} />
    </FormDialog>
  );
}

/** ثبت پیشرفت هر هدف طرح درمان در جلسه (AC-CLN-115.2). */
export function GoalProgressDialog({ encounter, onClose }: { encounter: ClnEncounterView; onClose: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const plans = useQuery({
    queryKey: clnKeys.section(session?.tenantId, encounter.patientId, 'plans'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/treatment-plans', { params: { path: { patientId: encounter.patientId } } })),
  });
  const active = (plans.data?.items ?? []).filter((plan) => plan.status !== 'Completed' && plan.status !== 'Discontinued' && plan.status !== 'Declined');
  const [planId, setPlanId] = useState('');
  const chosen = planId || active[0]?.id || '';
  const progress = useQuery({
    queryKey: psychKeys.goals(session?.tenantId, chosen),
    enabled: Boolean(chosen),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/treatment-plans/{id}/goal-progress', { params: { path: { id: chosen } } })),
  });
  const [ratings, setRatings] = useState<Record<number, ClnGoalProgressRating>>({});
  const [notes, setNotes] = useState<Record<number, string>>({});
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/encounters/{id}/goal-progress', {
          params: { path: { id: encounter.id } },
          headers: { 'Idempotency-Key': key.current() },
          body: { planId: chosen, entries: Object.entries(ratings).map(([index, rating]) => ({ goalIndex: Number(index), rating, note: notes[Number(index)]?.trim() || null })) },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (view) => {
      queryClient.setQueryData(psychKeys.goals(session?.tenantId, chosen), view);
      onClose();
    },
  });
  const last = (index: number) => [...(progress.data?.entries ?? [])].reverse().find((entry) => num(entry.goalIndex) === index);
  return (
    <FormDialog open titleKey="cln:psych.goal_progress" onSubmit={() => (Object.keys(ratings).length > 0 ? save.mutate() : undefined)} onClose={onClose} isDirty={Object.keys(ratings).length > 0} isSubmitting={save.isPending} submitError={save.error} maxWidth="md">
      {active.length === 0 && !plans.isPending ? (
        <Alert severity="info">{t('cln:psych.no_plan')}</Alert>
      ) : (
        <TextField select label={t('cln:psych.plan')} value={chosen} onChange={(event) => { setPlanId(event.target.value); setRatings({}); }} slotProps={{ htmlInput: { 'data-testid': 'cln-goal-plan' } }}>
          {active.map((plan) => (
            <MenuItem key={plan.id} value={plan.id}>
              {plan.title}
            </MenuItem>
          ))}
        </TextField>
      )}
      {(progress.data?.goals ?? []).map((goal, index) => (
        <Stack key={index} direction={{ xs: 'column', md: 'row' }} spacing={1} data-testid="cln-goal-row">
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2">{goal}</Typography>
            {last(index) ? (
              <Typography variant="caption" color="text.secondary">
                {t('cln:psych.last_rating', { rating: t(`cln:psych.ratings.${last(index)!.rating}`) })}
              </Typography>
            ) : null}
          </Box>
          <TextField select size="small" label={t('cln:psych.rating')} value={ratings[index] ?? ''} onChange={(event) => setRatings((current) => ({ ...current, [index]: event.target.value as ClnGoalProgressRating }))} sx={{ minWidth: 170 }} slotProps={{ htmlInput: { 'data-testid': `cln-goal-rating-${index}` } }}>
            {RATINGS.map((rating) => (
              <MenuItem key={rating} value={rating}>
                {t(`cln:psych.ratings.${rating}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" label={t('cln:psych.goal_note')} value={notes[index] ?? ''} onChange={(event) => setNotes((current) => ({ ...current, [index]: event.target.value }))} slotProps={{ htmlInput: { maxLength: 1000 } }} />
        </Stack>
      ))}
    </FormDialog>
  );
}

interface ChartPoint {
  at: string;
  value: number;
  band?: string | null;
  abnormal?: boolean;
}

/** نمودار سری زمانی SVG با بازه‌ها، نقاط جلسات و تغییر دارو (بدون کتابخانه؛ جدول دسترس‌پذیر کنار آن). */
export function SeriesChart({
  label,
  points,
  min,
  max,
  bands = [],
  normal,
  sessions = [],
  markers = [],
  compact = false,
  testId,
}: {
  label: string;
  points: ChartPoint[];
  min: number;
  max: number;
  bands?: { code: string; min: number; max: number; severity: string; label: string }[];
  normal?: { min: number; max: number } | null;
  sessions?: string[];
  markers?: { at: string; label: string }[];
  compact?: boolean;
  testId?: string;
}) {
  const { t } = useTranslation('cln');
  const { timeZone } = useDisplaySettings();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const bandFill = (severity: string) => {
    const tone =
      severity === 'none'
        ? theme.palette.success.main
        : severity === 'low' || severity === 'moderate'
          ? theme.palette.warning.main
          : severity === 'high' || severity === 'critical'
            ? theme.palette.error.main
            : theme.palette.text.primary;
    const amount =
      severity === 'critical' ? (dark ? 0.45 : 0.28) : severity === 'moderate' || severity === 'high' ? (dark ? 0.32 : 0.2) : dark ? 0.22 : 0.14;
    return alpha(tone, amount);
  };
  const line = theme.palette.primary.main;
  const abnormal = theme.palette.error.main;
  const width = compact ? 280 : 640;
  const height = compact ? 90 : 200;
  const pad = compact ? 6 : 24;
  const times = [...points.map((point) => Date.parse(point.at)), ...sessions.map((value) => Date.parse(value))].filter((value) => !Number.isNaN(value));
  const start = times.length ? Math.min(...times) : 0;
  const end = times.length ? Math.max(...times) : 1;
  const span = Math.max(1, end - start);
  const range = Math.max(1e-6, max - min);
  const x = (at: string) => pad + ((Date.parse(at) - start) / span) * (width - 2 * pad);
  const y = (value: number) => height - pad - ((Math.min(max, Math.max(min, value)) - min) / range) * (height - 2 * pad);
  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(point.at).toFixed(1)},${y(point.value).toFixed(1)}`).join(' ');
  return (
    <Box data-testid={testId} data-points={points.length}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label={label} style={{ direction: 'ltr', maxWidth: width }}>
        <title>{label}</title>
        {bands.map((band) => (
          <rect key={band.code} x={pad} width={width - 2 * pad} y={y(band.max)} height={Math.max(1, y(band.min) - y(band.max))} fill={bandFill(band.severity)}>
            <title>{band.label}</title>
          </rect>
        ))}
        {normal ? <rect x={pad} width={width - 2 * pad} y={y(normal.max)} height={Math.max(1, y(normal.min) - y(normal.max))} fill={alpha(theme.palette.success.main, dark ? 0.22 : 0.14)} /> : null}
        {sessions.map((session) => (
          <line key={session} x1={x(session)} x2={x(session)} y1={pad} y2={height - pad} stroke={theme.palette.text.disabled} strokeDasharray="3 3" data-testid="chart-session" />
        ))}
        {markers.map((marker, index) => (
          <path key={`${marker.at}-${index}`} d={`M${x(marker.at)},${height - pad} l-4,6 h8 z`} fill={theme.palette.secondary.main}>
            <title>{marker.label}</title>
          </path>
        ))}
        <path d={path} fill="none" stroke={line} strokeWidth={2} />
        {points.map((point, index) => (
          <circle key={index} cx={x(point.at)} cy={y(point.value)} r={compact ? 2.5 : 4} fill={point.abnormal ? abnormal : line} data-testid="chart-point" />
        ))}
      </svg>
      {!compact && (
        <Box component="table" sx={{ fontSize: 12, borderCollapse: 'collapse', '& td, & th': { px: 1, py: 0.25 } }}>
          <caption style={{ textAlign: 'start' }}>{label}</caption>
          <thead>
            <tr>
              <th scope="col">{t('cln:psych.chart_date')}</th>
              <th scope="col">{t('cln:psych.chart_value')}</th>
              <th scope="col">{t('cln:psych.chart_band')}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((point, index) => (
              <tr key={index}>
                <td>{toJalali(point.at, { timeZone })}</td>
                <td>
                  <bdi dir="ltr">{point.value}</bdi>
                </td>
                <td>{point.band ? (bands.find((band) => band.code === point.band)?.label ?? point.band) : point.abnormal ? t('cln:psych.abnormal') : ''}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      )}
    </Box>
  );
}

function outcomeChart(series: ClnOutcomeSeriesView, data: ClnOutcomesResponse, language: string, compact: boolean) {
  return (
    <SeriesChart
      key={series.key}
      testId="cln-outcome-chart"
      compact={compact}
      label={`${series.templateName} · ${series.scaleLabel}`}
      points={series.points.map((point) => ({ at: point.at, value: num(point.value) ?? 0, band: point.band }))}
      min={num(series.min) ?? 0}
      max={num(series.max) ?? 1}
      bands={series.bands.map((band) => ({ code: band.code, min: num(band.min) ?? 0, max: num(band.max) ?? 0, severity: band.severity, label: language === 'en' && band.labelEn ? band.labelEn : band.label }))}
      sessions={data.sessions}
      markers={data.medicationChanges.map((change) => ({ at: `${change.date}T00:00:00Z`, label: `${change.name} · ${change.change}` }))}
    />
  );
}

/** نمودار پیامد پرسشنامه‌ها (US-CLN-116؛ BR-CLN-114: سری جدا به ازای ابزار و نسخه). */
export function OutcomesPanel({ patientId, compact = false }: { patientId: string; compact?: boolean }) {
  const { t, i18n } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const outcomes = useQuery({
    queryKey: psychKeys.outcomes(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/outcomes', { params: { path: { patientId } } })),
    retry: false,
  });
  if (outcomes.error) {
    return compact ? null : <ErrorState error={outcomes.error} compact />;
  }
  const data = outcomes.data;
  const series = (data?.series ?? []).filter((item) => !compact || item.primary);
  return (
    <Stack spacing={1} data-testid="cln-outcomes">
      {!compact && (
        <Typography variant="subtitle1" component="h3">
          {t('cln:psych.outcomes')} <SensitiveBadge />
        </Typography>
      )}
      {data && series.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('cln:psych.no_outcomes')}
        </Typography>
      )}
      {data &&
        series.map((item) => (
          <Box key={item.key}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2">
                {item.instrument} · <bdi dir="ltr">v{item.scoringVersion}</bdi> · {item.scaleLabel}
              </Typography>
              {item.isSample ? <SampleBadge /> : null}
            </Stack>
            {outcomeChart(item, data, i18n.language, compact)}
          </Box>
        ))}
    </Stack>
  );
}

/** نمودار روند علائم حیاتی و شاخص‌های عددی با محدوده طبیعی (US-CLN-091). */
export function TrendsPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const trends = useQuery({
    queryKey: psychKeys.trends(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/trends', { params: { path: { patientId } } })),
  });
  if (trends.error) {
    return <ErrorState error={trends.error} compact />;
  }
  const series: ClnTrendSeriesView[] = trends.data?.series ?? [];
  return (
    <Stack spacing={2} data-testid="cln-trends">
      <Typography variant="subtitle1" component="h3">
        {t('cln:psych.trends')}
      </Typography>
      {trends.data && series.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('cln:psych.no_trends')}
        </Typography>
      )}
      <Grid container spacing={2}>
        {series.map((item) => {
          const values = item.points.map((point) => num(point.value) ?? 0);
          const normalMin = num(item.normalMin);
          const normalMax = num(item.normalMax);
          const low = Math.min(...values, normalMin ?? Infinity);
          const high = Math.max(...values, normalMax ?? -Infinity);
          const margin = Math.max(1, (high - low) * 0.1);
          return (
            <Grid key={item.key} size={{ xs: 12, lg: 6 }}>
              <SeriesChart
                testId={`cln-trend-${item.key}`}
                label={t(`cln:vitals.fields.${item.key}`, { defaultValue: item.key })}
                points={item.points.map((point) => ({ at: point.at, value: num(point.value) ?? 0, abnormal: point.abnormal }))}
                min={low - margin}
                max={high + margin}
                normal={normalMin !== null && normalMax !== null ? { min: normalMin, max: normalMax } : null}
                sessions={trends.data?.sessions ?? []}
              />
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
}

/** تب «ارزیابی خطر» پرونده: ارزیابی‌ها، برنامه ایمنی فعال و پیگیری‌ها (HighlySensitive؛ فقط سطرهای مجاز از سرور). */
export function RiskPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const [dialog, setDialog] = useState<'assess' | 'plan' | { followUp: string } | null>(null);
  const canAssess = access.accessMode === 'Full' && access.can('cln.risk.assess');
  const risk = useQuery({
    queryKey: psychKeys.risk(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/risk-assessments', { params: { path: { patientId } } })),
  });
  const plan = risk.data?.activeSafetyPlan;
  return (
    <Stack spacing={2} data-testid="cln-risk-panel">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
          {t('cln:psych.risk_assessments')} <SensitiveBadge />
        </Typography>
        {canAssess && (
          <>
            <Button onClick={() => setDialog('plan')} data-testid="cln-safety-plan-update">
              {t('cln:psych.update_safety_plan')}
            </Button>
            <Button variant="contained" color="error" onClick={() => setDialog('assess')} data-testid="cln-risk-new">
              {t('cln:psych.risk_assessment')}
            </Button>
          </>
        )}
      </Stack>
      {risk.error ? <ErrorState error={risk.error} compact /> : null}
      {(risk.data?.items ?? []).map((item) => (
        <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }} data-testid="cln-risk-row">
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
            <RiskLevelChip level={item.level} />
            <Typography variant="body2">
              {toJalaliDateTime(item.assessedAt, { timeZone })} · {item.assessedByName ?? '—'}
            </Typography>
            {item.followUpStatus === 'Open' && (
              <Chip size="small" color={item.followUpOverdue ? 'error' : 'warning'} label={t('cln:psych.follow_up_due', { at: item.followUpDueAt ? toJalaliDateTime(item.followUpDueAt, { timeZone }) : '' })} />
            )}
            {item.followUpStatus === 'Completed' && <Chip size="small" color="success" variant="outlined" label={t('cln:psych.follow_up_done')} />}
            <Box sx={{ flexGrow: 1 }} />
            {canAssess && item.followUpStatus === 'Open' && (
              <Button size="small" onClick={() => setDialog({ followUp: item.id })} data-testid="cln-risk-follow-up">
                {t('cln:psych.complete_follow_up')}
              </Button>
            )}
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {item.riskFactors.map((code) => t(`cln:psych.factors.${code}`)).join(t('cln:common.list_separator'))}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {item.judgment}
          </Typography>
        </Paper>
      ))}
      {risk.data && risk.data.items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('cln:psych.no_risk')}
        </Typography>
      )}
      {plan && (
        <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-active-safety-plan">
          <Typography variant="subtitle2">{t('cln:psych.active_safety_plan', { version: num(plan.version) })}</Typography>
          <Typography variant="body2">
            {t('cln:psych.warning_signs')}: {plan.warningSigns.join(t('cln:common.list_separator'))}
          </Typography>
          <Typography variant="body2">
            {t('cln:psych.coping')}: {plan.copingStrategies.join(t('cln:common.list_separator'))}
          </Typography>
          <Typography variant="body2">
            {t('cln:psych.contacts')}: {plan.contacts.map((contact) => [contact.name, contact.phone].filter(Boolean).join(' ')).join(t('cln:common.list_separator'))}
          </Typography>
          <Typography variant="body2">
            {t('cln:psych.emergency_contact')}: {plan.emergencyContact.name} <bdi dir="ltr">{plan.emergencyContact.phone}</bdi>
          </Typography>
        </Paper>
      )}
      {dialog === 'assess' && <RiskAssessmentDialog patientId={patientId} onClose={() => setDialog(null)} />}
      {dialog === 'plan' && <SafetyPlanDialog patientId={patientId} onClose={() => setDialog(null)} />}
      {dialog && typeof dialog === 'object' && <RiskFollowUpDialog assessmentId={dialog.followUp} onClose={() => setDialog(null)} />}
    </Stack>
  );
}

/** پنل روان کنار یادداشت (FLOW-07b): دکمه ثابت ارزیابی خطر، آخرین سطح و نمودار کوچک پیامد. */
export function PsychPanel({ encounter, onChanged }: { encounter: ClnEncounterView; onChanged?: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const [dialog, setDialog] = useState<'risk' | 'goals' | null>(null);
  const full = access.accessMode === 'Full';
  const canAssess = full && access.can('cln.risk.assess') && encounter.status !== 'Cancelled';
  const canGoals = full && access.can('cln.note.write') && encounter.status !== 'Cancelled';
  const canSensitive = access.can('cln.record.view_sensitive');
  const risk = useQuery({
    queryKey: psychKeys.risk(session?.tenantId, encounter.patientId),
    enabled: canAssess || canSensitive,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/risk-assessments', { params: { path: { patientId: encounter.patientId } } })),
    retry: false,
  });
  const latest = risk.data?.items[0];
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-psych-panel">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
          {t('cln:psych.panel_title')}
        </Typography>
        {latest ? <RiskLevelChip level={latest.level} /> : null}
        {canGoals && (
          <Button onClick={() => setDialog('goals')} data-testid="cln-goal-progress">
            {t('cln:psych.goal_progress')}
          </Button>
        )}
        {canAssess && (
          <Button variant="contained" color="error" onClick={() => setDialog('risk')} data-testid="cln-risk-assess">
            {t('cln:psych.risk_assessment')}
          </Button>
        )}
      </Stack>
      {canSensitive && <OutcomesPanel patientId={encounter.patientId} compact />}
      {dialog === 'risk' && <RiskAssessmentDialog patientId={encounter.patientId} encounterId={encounter.id} onClose={() => setDialog(null)} onSaved={onChanged} />}
      {dialog === 'goals' && <GoalProgressDialog encounter={encounter} onClose={() => setDialog(null)} />}
    </Paper>
  );
}

/** پیش‌نمایش انتخاب یادداشت‌ها برای خروجی (AC-CLN-100.2): حساس فقط با انتخاب صریح و تأیید نویسنده یا Manager. */
export function ExportSelectionDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const key = useIdempotencyKey();
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const preview = useQuery({
    queryKey: ['clinical', 'export-selection', patientId, selected.join(',')],
    queryFn: () => unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/export-selection', { params: { path: { patientId } }, body: { includeSensitiveNoteIds: selected } })),
  });
  const approve = useMutation({
    mutationFn: (noteId: string) => unwrap(tenantApi.POST('/api/v1/clinical/notes/{id}:approve-export', { params: { path: { id: noteId } }, headers: { 'Idempotency-Key': key.current() }, body: { reason: reason.trim() || null } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => void preview.refetch(),
  });
  const canApprove = Boolean(reason.trim());
  return (
    <FormDialog open titleKey="cln:psych.export_selection" onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={false} submitError={approve.error} submitKey="cln:common.close" maxWidth="md">
      <Alert severity="info">{t('cln:psych.export_hint')}</Alert>
      <TextField size="small" label={t('cln:psych.approval_reason')} value={reason} onChange={(event) => setReason(event.target.value)} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'cln-export-reason' } }} />
      {(preview.data?.items ?? []).map((item) => {
        const sensitive = item.sensitivity === 'HighlySensitive';
        return (
          <Stack key={item.noteId} direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid="cln-export-row" data-included={item.included}>
            {sensitive ? (
              <FormControlLabel
                control={<Checkbox checked={selected.includes(item.noteId)} onChange={(event) => setSelected((current) => (event.target.checked ? [...current, item.noteId] : current.filter((id) => id !== item.noteId)))} />}
                label={toJalaliDateTime(item.startedAt, { timeZone })}
              />
            ) : (
              <Typography variant="body2" sx={{ minWidth: 180 }}>
                {toJalaliDateTime(item.startedAt, { timeZone })}
              </Typography>
            )}
            {sensitive ? <SensitiveBadge /> : null}
            <Chip size="small" color={item.included ? 'success' : 'default'} label={item.included ? t('cln:psych.included') : t(`cln:psych.export_reason.${item.reason ?? 'not_selected'}`)} />
            <Box sx={{ flexGrow: 1 }} />
            {sensitive && !item.approved && (
              <Button size="small" disabled={!canApprove || approve.isPending} onClick={() => approve.mutate(item.noteId)} data-testid="cln-export-approve">
                {t('cln:psych.approve_export')}
              </Button>
            )}
          </Stack>
        );
      })}
    </FormDialog>
  );
}
